import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Eye, Trash2 } from "lucide-react";
import SidebarLayout from "../../components/SidebarLayout";
import Modal from "../../components/Modal";
import ConfirmModal from "../../components/ConfirmModal";
import { BACKEND_URL } from "../../config";
import { useAuth } from "../../modules/auth/authContext";

/* ------------------ Types ------------------ */
type EmployeeType = "DOCTOR" | "FRONT_DESK" | "PHARMACY" | "ADMIN" | "LAB_TECHNICIAN" | "IT_STAFF" | "BILLING" | "STAFF" | "EMPLOYEE";

interface Employee {
  id: number;
  name: string;
  type: EmployeeType;
  specializations?: string;
  description?: string;
  contact?: {
    phone?: string;
    email?: string;
    gender?: string;
    address?: string;
    dob?: string;
    name?: string;
  };
}

const employeeTypeOptions = [
  { value: "DOCTOR", label: "Doctor" },
  { value: "FRONT_DESK", label: "Front Desk" },
  { value: "PHARMACY", label: "Pharmacy" },
  { value: "ADMIN", label: "Admin" },
  { value: "LAB_TECHNICIAN", label: "Lab Technician" },
  { value: "IT_STAFF", label: "IT Staff" },
  { value: "BILLING", label: "Billing" },
];

const employeeTypeLabel = (value?: string) => employeeTypeOptions.find((option) => option.value === value)?.label || value || "-";

/* ------------------ Component ------------------ */
const EmployeesList: React.FC = () => {
  const [items, setItems] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState<{
    name?: string;
    type?: string;
    phone?: string;
    email?: string;
  }>({});

  const [modal, setModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [confirmDelete, setConfirmDelete] = useState<{
    open: boolean;
    id?: number;
  }>({ open: false });

  const { can } = useAuth();
  const canEditEmployees = can("employees.edit");

  /* ------------------ Load ------------------ */
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/employees/`, {
          credentials: "include",
        });
        if (res.ok) {
          setItems(await res.json());
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  /* ------------------ Handlers ------------------ */
  const handleFilter = (k: string, v: string) =>
    setFilters((p) => ({ ...p, [k]: v }));

  const openDeleteConfirm = (id: number) =>
    setConfirmDelete({ open: true, id });

  const handleDelete = async (id?: number) => {
    setConfirmDelete({ open: false });
    if (!id) return;

    const res = await fetch(`${BACKEND_URL}/api/employees/${id}`, {
      method: "DELETE",
      credentials: "include",
    });

    if (res.ok) {
      setItems((p) => p.filter((x) => x.id !== id));
      setModal({
        open: true,
        title: "Deleted",
        message: "Employee deleted successfully",
      });
    }
  };

  /* ------------------ Filters ------------------ */
  const filteredItems = useMemo(() => {
    return items.filter((e) => {
      if (
        filters.name &&
        !e.name.toLowerCase().includes(filters.name.toLowerCase())
      )
        return false;

      if (filters.type && e.type !== filters.type) return false;

      if (
        filters.phone &&
        !e.contact?.phone?.includes(filters.phone)
      )
        return false;

      if (
        filters.email &&
        !e.contact?.email?.toLowerCase().includes(filters.email.toLowerCase())
      )
        return false;

      return true;
    });
  }, [items, filters]);

  /* ------------------ UI ------------------ */
  return (
    <SidebarLayout title="Employees">
      {loading ? (
        <div>Loading...</div>
      ) : (
        <>
          <Modal
            open={modal.open}
            title={modal.title}
            message={modal.message}
            onClose={() => setModal({ ...modal, open: false })}
          />

          <ConfirmModal
            open={confirmDelete.open}
            message="Delete this employee?"
            onCancel={() => setConfirmDelete({ open: false })}
            onConfirm={() => handleDelete(confirmDelete.id)}
          />

          {/* Filters */}
          <div className="bg-surface-light p-4 rounded border">
            <div className="mb-4 grid grid-cols-1 sm:grid-cols-4 gap-2">
              <input
                className="p-2 border rounded"
                placeholder="Search name"
                value={filters.name || ""}
                onChange={(e) => handleFilter("name", e.target.value)}
              />

              <select
                className="p-2 border rounded"
                value={filters.type || ""}
                onChange={(e) => handleFilter("type", e.target.value)}
              >
                <option value="">All Types</option>
                {employeeTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <input
                className="p-2 border rounded"
                placeholder="Search phone"
                value={filters.phone || ""}
                onChange={(e) => handleFilter("phone", e.target.value)}
              />

              <input
                className="p-2 border rounded"
                placeholder="Search email"
                value={filters.email || ""}
                onChange={(e) => handleFilter("email", e.target.value)}
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[820px] text-left">
                <thead>
                  <tr className="text-xs text-subtext-light">
                    <th className="p-2 text-left">Name</th>
                    <th className="p-2 text-left">Type</th>
                    <th className="p-2 text-left">Phone</th>
                    <th className="p-2 text-left">Email</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {filteredItems.map((e) => (
                    <tr key={e.id} className="border-t">
                      <td className="p-2 font-medium">{e.name}</td>
                      <td className="p-2">{employeeTypeLabel(e.type)}</td>
                      <td className="p-2">{e.contact?.phone || "-"}</td>
                      <td className="p-2">{e.contact?.email || "-"}</td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          {/* VIEW */}
                          <Link
                            to="/employees/new"
                            state={{ employee: e, view: true }}
                            className="hover:bg-primary/10 p-1.5 rounded"
                          >
                            <Eye size={18} />
                          </Link>

                          {/* EDIT */}
                          {canEditEmployees && (
                            <Link
                              to="/employees/new"
                              state={{ employee: e }}
                              className="hover:bg-primary/10 p-1.5 rounded"
                            >
                              <Pencil size={18} />
                            </Link>
                          )}

                          {/* DELETE */}
                          {canEditEmployees && (
                            <button
                              onClick={() => openDeleteConfirm(e.id)}
                              className="p-1.5 text-alert-red"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </SidebarLayout>
  );
};

export default EmployeesList;
